import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';

/**
 * El canal SMS (BulkGate): qué salió, qué llegó, qué falló, cuánto costó y
 * quién contestó.
 *
 * Todo sale de `smsmessages`, que existe desde el 09/10/2026. Lo enviado antes
 * no tiene informe de entrega, así que no aparece aquí.
 *
 * Tres cosas que no son obvias:
 *
 *   · «Enviados» no incluye los RECHAZADOS: BulkGate no los acepta (número
 *     inválido, lista negra…), no salen y no se cobran. Van aparte, y en la
 *     lista de no entregados;
 *   · «Sin confirmar» es lo que BulkGate aceptó y de lo que el operador aún no
 *     ha dicho nada. Si se queda ahí días, es que no llegó;
 *   · la respuesta se cuenta por EXPEDIENTE: tres SMS del mismo deudor son un
 *     deudor que contesta, no tres.
 */

const RANGOS = [
  { dias: 7, nombre: '7 días' },
  { dias: 14, nombre: '14 días' },
  { dias: 30, nombre: '30 días' },
  { dias: 90, nombre: '90 días' },
];

// Por debajo de esto el saldo se pinta en rojo. Es el mismo umbral por defecto
// del aviso por Slack (SMS_BALANCE_ALERT_CREDITS).
const SALDO_BAJO = 250;

const MOTIVOS = {
  failed: 'No llegó',
  rejected: 'Rechazado',
  invalid_number: 'Número inválido',
  blacklisted: 'En lista negra',
  duplicity_message: 'Duplicado',
  invalid_sender: 'Remitente inválido',
  error: 'Error de BulkGate',
};

const euros = n =>
  typeof n === 'number'
    ? n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })
    : '—';

const pct = n => (typeof n === 'number' ? `${n.toLocaleString('es-ES')}%` : '—');

const fechaCorta = dia => {
  const [a, m, d] = dia.split('-');
  return new Date(Date.UTC(+a, +m - 1, +d)).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
};

const fechaHora = f =>
  new Date(f).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const Tarjeta = ({ titulo, valor, detalle, alerta = false }) => (
  <div className={`rounded-lg border px-4 py-3 ${alerta ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
    <p className='text-xs font-medium text-slate-500 uppercase'>{titulo}</p>
    <p className={`text-xl font-semibold tabular-nums mt-1 ${alerta ? 'text-red-700' : 'text-slate-900'}`}>{valor}</p>
    {detalle && <p className='text-xs text-slate-500 mt-0.5'>{detalle}</p>}
  </div>
);

const Seccion = ({ titulo, subtitulo, children }) => (
  <div className='bg-white rounded-lg border border-slate-200'>
    <div className='px-6 py-4 border-b border-slate-200'>
      <h3 className='text-sm font-semibold text-slate-900'>{titulo}</h3>
      {subtitulo && <p className='text-xs text-slate-500 mt-0.5'>{subtitulo}</p>}
    </div>
    <div className='overflow-x-auto'>{children}</div>
  </div>
);

const th = 'px-3 py-2 text-xs font-medium text-slate-500 uppercase';
const td = 'px-3 py-2 tabular-nums';

const Saldo = ({ saldo }) => {
  if (!saldo || saldo.error) {
    return <Tarjeta titulo='Saldo' valor='—' detalle='No se pudo consultar a BulkGate' alerta />;
  }
  return (
    <Tarjeta
      titulo='Saldo'
      valor={euros(saldo.euros)}
      detalle={`${saldo.credit.toLocaleString('es-ES', { maximumFractionDigits: 2 })} créditos`}
      alerta={saldo.credit < SALDO_BAJO}
    />
  );
};

const SmsKpis = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [dias, setDias] = useState(14);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/sms/kpis?dias=${dias}`);
      setDatos(data.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando && !datos) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 px-6 py-8'>
        <p className='text-sm text-slate-500'>Cargando el canal SMS…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 px-6 py-6'>
        <p className='text-sm text-red-700'>{error}</p>
      </div>
    );
  }

  const t = datos.totales;
  const saldoBajo = datos.saldo && !datos.saldo.error && datos.saldo.credit < SALDO_BAJO;

  return (
    <div className='space-y-6'>
      <div className='bg-white rounded-lg border border-slate-200'>
        <div className='px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4'>
          <div>
            <h2 className='text-base font-semibold text-slate-900'>SMS · BulkGate</h2>
            <p className='text-sm text-slate-500 mt-0.5'>
              Lo que salió, lo que llegó y quién contestó. Sale desde el número de sistema, que recibe las respuestas.
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <div className='inline-flex rounded-lg border border-slate-200 overflow-hidden'>
              {RANGOS.map(r => (
                <button
                  key={r.dias}
                  type='button'
                  onClick={() => setDias(r.dias)}
                  className={`px-3 py-1.5 text-sm ${dias === r.dias ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  {r.nombre}
                </button>
              ))}
            </div>
            <button
              type='button'
              onClick={cargar}
              disabled={cargando}
              aria-label='Recargar'
              className='p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50'
            >
              <ArrowPathIcon className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {saldoBajo && (
          <div className='mx-6 mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2'>
            <ExclamationTriangleIcon className='w-5 h-5 text-red-600 flex-shrink-0' />
            <p className='text-sm text-red-800'>
              Queda poco saldo. Cuando se acabe, los SMS dejarán de salir sin avisar.{' '}
              <a href='https://portal.bulkgate.com/top/up' target='_blank' rel='noreferrer' className='underline'>
                Recargar en BulkGate
              </a>
            </p>
          </div>
        )}

        <div className='p-6 grid grid-cols-2 md:grid-cols-4 gap-3'>
          <Saldo saldo={datos.saldo} />
          <Tarjeta titulo='Enviados' valor={t.enviados} detalle={`Coste ${euros(t.coste_eur)}`} />
          <Tarjeta titulo='Entregados' valor={t.entregados} detalle={pct(t.pct_entregados)} />
          <Tarjeta
            titulo='No entregados'
            valor={t.fallidos + t.rechazados}
            detalle={`${t.fallidos} no llegaron · ${t.rechazados} rechazados`}
            alerta={t.fallidos + t.rechazados > 0}
          />
          <Tarjeta titulo='Sin confirmar' valor={t.sin_confirmar} detalle='El operador aún no ha dicho nada' />
          <Tarjeta
            titulo='Contestan'
            valor={t.expedientes_con_respuesta}
            detalle={`${pct(t.pct_respuesta)} de ${t.expedientes_contactados} expedientes`}
          />
          <Tarjeta titulo='Respuestas' valor={t.respuestas} detalle='Mensajes recibidos' />
        </div>
      </div>

      <Seccion
        titulo='Por día'
        subtitulo='Por el día en que se pidió el envío, aunque el informe de entrega llegue después.'
      >
        <table className='min-w-full text-sm'>
          <thead className='bg-slate-50'>
            <tr>
              <th className={`${th} text-left`}>Día</th>
              <th className={`${th} text-right`}>Enviados</th>
              <th className={`${th} text-right`}>Entregados</th>
              <th className={`${th} text-right`}>No llegaron</th>
              <th className={`${th} text-right`}>Rechazados</th>
              <th className={`${th} text-right`}>Sin confirmar</th>
              <th className={`${th} text-right`}>Respuestas</th>
              <th className={`${th} text-right`}>Coste</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {[...datos.por_dia].reverse().map(f => (
              <tr key={f.fecha}>
                <td className='px-3 py-2 text-slate-700'>{fechaCorta(f.fecha)}</td>
                <td className={`${td} text-right text-slate-900`}>{f.enviados || '·'}</td>
                <td className={`${td} text-right text-slate-600`}>{f.entregados || '·'}</td>
                <td className={`${td} text-right ${f.fallidos ? 'text-amber-700 font-semibold' : 'text-slate-300'}`}>
                  {f.fallidos || '·'}
                </td>
                <td className={`${td} text-right ${f.rechazados ? 'text-amber-700 font-semibold' : 'text-slate-300'}`}>
                  {f.rechazados || '·'}
                </td>
                <td className={`${td} text-right text-slate-500`}>{f.sin_confirmar || '·'}</td>
                <td className={`${td} text-right text-slate-600`}>{f.respuestas || '·'}</td>
                <td className={`${td} text-right text-slate-500`}>{f.coste_eur ? euros(f.coste_eur) : '·'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Seccion>

      <Seccion titulo='Por país' subtitulo='A dónde van los SMS. Fuera de España cada SMS cuesta varias veces más.'>
        {datos.por_pais.length === 0 ? (
          <p className='px-6 py-4 text-sm text-slate-500'>No se ha enviado nada en este periodo.</p>
        ) : (
          <table className='min-w-full text-sm'>
            <thead className='bg-slate-50'>
              <tr>
                <th className={`${th} text-left`}>País</th>
                <th className={`${th} text-right`}>Enviados</th>
                <th className={`${th} text-right`}>Entregados</th>
                <th className={`${th} text-right`}>No entregados</th>
                <th className={`${th} text-right`}>% entregado</th>
                <th className={`${th} text-right`}>Coste</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {datos.por_pais.map(p => (
                <tr key={p.pais}>
                  <td className='px-3 py-2 text-slate-700'>{p.pais}</td>
                  <td className={`${td} text-right text-slate-900`}>{p.enviados}</td>
                  <td className={`${td} text-right text-slate-600`}>{p.entregados}</td>
                  <td className={`${td} text-right ${p.fallidos ? 'text-amber-700 font-semibold' : 'text-slate-300'}`}>
                    {p.fallidos || '·'}
                  </td>
                  <td className={`${td} text-right text-slate-600`}>{pct(p.pct_entregados)}</td>
                  <td className={`${td} text-right text-slate-500`}>{euros(p.coste_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Seccion>

      <Seccion
        titulo={`No entregados (${datos.no_entregados.length})`}
        subtitulo='Los que no llegaron al móvil y los que BulkGate no aceptó. Normalmente el número está mal: revísalo en la ficha.'
      >
        {datos.no_entregados.length === 0 ? (
          <p className='px-6 py-4 text-sm text-slate-500'>Ninguno en este periodo.</p>
        ) : (
          <table className='min-w-full text-sm'>
            <thead className='bg-slate-50'>
              <tr>
                <th className={`${th} text-left`}>Cuándo</th>
                <th className={`${th} text-left`}>Deudor</th>
                <th className={`${th} text-left`}>Cliente</th>
                <th className={`${th} text-left`}>Teléfono</th>
                <th className={`${th} text-left`}>Motivo</th>
                <th className={`${th} text-left`}>Texto</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {datos.no_entregados.map(m => (
                <tr key={m.id}>
                  <td className='px-3 py-2 text-slate-600 whitespace-nowrap'>{fechaHora(m.fecha)}</td>
                  <td className='px-3 py-2'>
                    {m.expedient_id ? (
                      <Link to={`/view/${m.expedient_id}`} className='text-indigo-700 hover:underline'>
                        {m.deudor || 'Ver expediente'}
                      </Link>
                    ) : (
                      <span className='text-slate-400'>—</span>
                    )}
                  </td>
                  <td className='px-3 py-2 text-slate-600'>{m.cliente || '—'}</td>
                  <td className='px-3 py-2 text-slate-600 tabular-nums'>+{m.telefono}</td>
                  <td className='px-3 py-2 text-amber-700'>{MOTIVOS[m.motivo] || MOTIVOS[m.estado] || m.motivo}</td>
                  <td className='px-3 py-2 text-slate-500 max-w-xs truncate' title={m.texto}>
                    {m.texto}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Seccion>

      <Seccion
        titulo={`Respuestas sin expediente (${datos.sin_expediente.length})`}
        subtitulo='Alguien contestó a un SMS desde un número que no tenemos en ningún deudor. No crean tarea: hay que mirarlas a mano.'
      >
        {datos.sin_expediente.length === 0 ? (
          <p className='px-6 py-4 text-sm text-slate-500'>Ninguna en este periodo.</p>
        ) : (
          <table className='min-w-full text-sm'>
            <thead className='bg-slate-50'>
              <tr>
                <th className={`${th} text-left`}>Cuándo</th>
                <th className={`${th} text-left`}>Teléfono</th>
                <th className={`${th} text-left`}>País</th>
                <th className={`${th} text-left`}>Mensaje</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {datos.sin_expediente.map(m => (
                <tr key={m.id}>
                  <td className='px-3 py-2 text-slate-600 whitespace-nowrap'>{fechaHora(m.fecha)}</td>
                  <td className='px-3 py-2 text-slate-600 tabular-nums'>+{m.telefono}</td>
                  <td className='px-3 py-2 text-slate-600'>{m.pais || '—'}</td>
                  <td className='px-3 py-2 text-slate-800'>{m.texto}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Seccion>
    </div>
  );
};

export default SmsKpis;
